import React, { useState } from 'react';
import BackButton from '../components/BackButton';
import Footer from '../components/Footer';
import '../App.scss';

type OperationType = 'addition' | 'subtraction' | 'multiplication' | 'division';

const BasicOperations: React.FC = () => {
  const [activeOperation, setActiveOperation] = useState<OperationType>('addition');
  
  const renderOperationContent = () => {
    switch (activeOperation) {
      case 'addition':
        return (
          <div className="tutorial-section">
            <h2>Addition (+)</h2>
            <div className="explanation">
              <p>Addition means putting things together and finding the total.</p>
              <p>The + sign means we are adding numbers together.</p>
              <p>When we add two numbers, the result is called the <strong>sum</strong>.</p>
            </div>
            
            <div className="example">
              <h3>How to Add Numbers</h3>
              <ol>
                <li>Place the numbers one above the other, aligning the ones, tens, hundreds, etc.</li>
                <li>Start adding from the rightmost column (ones).</li>
                <li>If the sum in any column is 10 or more, carry over to the next column.</li>
                <li>Continue adding each column, including any carried values.</li>
              </ol>
              
              <div className="example-visual">
                <div className="math-problem">
                  <div className="problem-row">
                    <span className="carry">1</span>
                    <span></span>
                  </div>
                  <div className="problem-row">
                    <span>2</span>
                    <span>8</span>
                  </div>
                  <div className="problem-row operation-row">
                    <span>+</span>
                    <span>5</span>
                  </div>
                  <div className="problem-row result-row">
                    <span>3</span>
                    <span>3</span>
                  </div>
                </div>
                <p className="example-explanation">
                  Step 1: Add 8 + 5 = 13. Write 3 and carry 1.<br/>
                  Step 2: Add 2 + 0 + 1 (carried) = 3.<br/>
                  The sum is 33.
                </p>
              </div>
            </div>
            
            <div className="real-life-examples">
              <h3>Real-Life Examples</h3>
              <ul>
                <li>Counting how many toys you have in total</li>
                <li>Finding the total cost of items when shopping</li>
                <li>Counting how many students are in a classroom</li>
              </ul>
            </div>
          </div>
        );
        
      case 'subtraction':
        return (
          <div className="tutorial-section">
            <h2>Subtraction (-)</h2>
            <div className="explanation">
              <p>Subtraction means taking away one number from another to find the difference.</p>
              <p>The - sign means we are subtracting one number from another.</p>
              <p>When we subtract, the result is called the <strong>difference</strong>.</p>
            </div>
            
            <div className="example">
              <h3>How to Subtract Numbers</h3>
              <ol>
                <li>Place the larger number on top and the smaller number below.</li>
                <li>Start subtracting from the rightmost column (ones).</li>
                <li>If you can't subtract (the top digit is smaller), borrow 1 from the next column.</li>
                <li>Continue subtracting each column.</li>
              </ol>
              
              <div className="example-visual">
                <div className="math-problem">
                  <div className="problem-row">
                    <span className="borrow">4</span>
                    <span className="borrow">12</span>
                  </div>
                  <div className="problem-row">
                    <span>5</span>
                    <span>2</span>
                  </div>
                  <div className="problem-row operation-row">
                    <span>-</span>
                    <span>7</span>
                  </div>
                  <div className="problem-row result-row">
                    <span>4</span>
                    <span>5</span>
                  </div>
                </div>
                <p className="example-explanation">
                  Step 1: 2 - 7? We can't do that directly, so borrow 1 from 5.<br/>
                  Step 2: 4 (from 5) and 12 (from 2 + 10) - 7 = 5.<br/>
                  The difference is 45.
                </p>
              </div>
            </div>
            
            <div className="real-life-examples">
              <h3>Real-Life Examples</h3>
              <ul>
                <li>Finding how much money is left after buying something</li>
                <li>Calculating how many more items you need to collect</li>
                <li>Finding the age difference between siblings</li>
              </ul>
            </div>
          </div>
        );
        
      case 'multiplication':
        return (
          <div className="tutorial-section">
            <h2>Multiplication (×)</h2>
            <div className="explanation">
              <p>Multiplication is a faster way of adding the same number multiple times.</p>
              <p>The × sign means we are multiplying numbers.</p>
              <p>When we multiply two numbers, the result is called the <strong>product</strong>.</p>
            </div>
            
            <div className="example">
              <h3>How to Multiply Numbers</h3>
              <ol>
                <li>Place the larger number on top and the smaller number below.</li>
                <li>Multiply each digit of the bottom number with each digit of the top number, from right to left.</li>
                <li>If the result has more than one digit, carry over to the next column.</li>
                <li>Add all partial products to get the final result.</li>
              </ol>
              
              <div className="example-visual">
                <div className="math-problem">
                  <div className="problem-row">
                    <span className="carry">1</span>
                    <span></span>
                  </div>
                  <div className="problem-row">
                    <span>2</span>
                    <span>5</span>
                  </div>
                  <div className="problem-row operation-row">
                    <span>×</span>
                    <span>4</span>
                  </div>
                  <div className="problem-row result-row">
                    <span>1</span>
                    <span>0</span>
                    <span>0</span>
                  </div>
                </div>
                <p className="example-explanation">
                  Step 1: 4 × 5 = 20. Write 0 and carry 2.<br/>
                  Step 2: 4 × 2 = 8, plus 2 (carried) = 10.<br/>
                  The product is 100.
                </p>
              </div>
            </div>
            
            <div className="real-life-examples">
              <h3>Real-Life Examples</h3>
              <ul>
                <li>Calculating the total cost of multiple identical items</li>
                <li>Finding the area of a rectangle</li>
                <li>Determining how many total seats are in rows and columns</li>
              </ul>
            </div>
          </div>
        );
        
      case 'division':
        return (
          <div className="tutorial-section">
            <h2>Division (÷)</h2>
            <div className="explanation">
              <p>Division means splitting a number into equal parts.</p>
              <p>The ÷ sign means we are dividing one number by another.</p>
              <p>When we divide, the result is called the <strong>quotient</strong>, and what's left over is called the <strong>remainder</strong>.</p>
            </div>
            
            <div className="example">
              <h3>How to Divide Numbers</h3>
              <ol>
                <li>Set up the division problem with the divisor (the number dividing by) outside and the dividend (the number being divided) inside.</li>
                <li>Divide each digit of the dividend by the divisor, starting from the left.</li>
                <li>Write the quotient above and multiply it by the divisor, then subtract from the dividend digit.</li>
                <li>Bring down the next digit and repeat the process.</li>
              </ol>
              
              <div className="example-visual">
                <div className="division-problem">
                  <div className="quotient">24</div>
                  <div className="divisor">4</div>
                  <div className="division-symbol">)</div>
                  <div className="dividend">96</div>
                </div>
                <p className="example-explanation">
                  Step 1: 4 into 9 goes 2 times. 2 × 4 = 8. 9 - 8 = 1.<br/>
                  Step 2: Bring down 6. 4 into 16 goes 4 times. 4 × 4 = 16. 16 - 16 = 0.<br/>
                  The quotient is 24 with no remainder.
                </p>
              </div>
            </div>
            
            <div className="real-life-examples">
              <h3>Real-Life Examples</h3>
              <ul>
                <li>Sharing candies equally among friends</li>
                <li>Dividing money equally in a group</li>
                <li>Finding how many weeks are in a certain number of days</li>
              </ul>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="basic-operations-page">
      <div className="game-header">
        <BackButton />
      </div>
      <h1>Basic Operations</h1>
      
      <div className="page-content">
        <p className="intro">
          Learn the four basic operations of mathematics: addition, subtraction, multiplication, and division.
        </p>
        
        <div className="operation-tabs">
          <button 
            className={`tab ${activeOperation === 'addition' ? 'active' : ''}`} 
            onClick={() => setActiveOperation('addition')}
          >
            Addition (+)
          </button>
          <button 
            className={`tab ${activeOperation === 'subtraction' ? 'active' : ''}`} 
            onClick={() => setActiveOperation('subtraction')}
          >
            Subtraction (-)
          </button>
          <button 
            className={`tab ${activeOperation === 'multiplication' ? 'active' : ''}`} 
            onClick={() => setActiveOperation('multiplication')}
          >
            Multiplication (×)
          </button>
          <button 
            className={`tab ${activeOperation === 'division' ? 'active' : ''}`} 
            onClick={() => setActiveOperation('division')}
          >
            Division (÷)
          </button>
        </div>
        
        <div className="operation-content">
          {renderOperationContent()}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BasicOperations; 